import { Injectable } from '@angular/core';
import { Module } from '../model/ModuleModel';
import { EmptyStateComponent } from '../components/empty-state/empty-state.component';

type ModulesByGroup = { [groupNumber: number]: Module[] };

enum EmptyStateStatus {
  PENDING_AJAX = 'PENDING_AJAX',
  SHOW = 'SHOW',
  HIDE = 'HIDE'
}

interface GroupStatus {
  status: EmptyStateStatus;
  hasAjaxModules: boolean;
  hasModulesWithTitle: boolean;
  showedModule?: string;
}

interface ModuleContainerStatus {
  groups: { [groupNumber: number]: GroupStatus };
  activeGroupNumber?: number;
}

@Injectable({
  providedIn: 'root'
})
export class EmtpyStateService {
  private moduleContainerStatuses: { [containerId: string]: ModuleContainerStatus } = {};

  shouldShowEmptyState(module: Module, moduleContainerId: string): boolean {
    if (!this.isEmptyStateCandidate(module) || !moduleContainerId || module?.id !== EmptyStateComponent.MODULE_ID) {
      return false;
    }

    const containerStatus: ModuleContainerStatus = this.moduleContainerStatuses[moduleContainerId] || {} as ModuleContainerStatus;
    const groupNumber: number = module.empty_state_data!.group_number;
    const groupStatus: GroupStatus = containerStatus.groups[groupNumber];

    if (containerStatus.activeGroupNumber === groupNumber && this.shouldShowInsideGroup(module, groupStatus)) {
      groupStatus.showedModule = module.unique_id;
      return true;
    } else {
      return false;
    }
  }

  analyzeEmptyStates(modules: Module[], moduleContainerId: string): void {
    if (!moduleContainerId || !modules?.length) return;

    this.moduleContainerStatuses[moduleContainerId] = { groups: {}, activeGroupNumber: undefined };

    const groupedModules: ModulesByGroup = this.groupModulesByGroupNumber(modules);

    for (const [groupNumberStr, groupModules] of Object.entries(groupedModules)) {
      const groupNumber: number = parseInt(groupNumberStr, 10);
      const groupStatus: GroupStatus = this.analyzeGroup(groupModules);
      
      this.moduleContainerStatuses[moduleContainerId].groups[groupNumber] = groupStatus;
      if (groupStatus.status === EmptyStateStatus.SHOW && this.moduleContainerStatuses[moduleContainerId].activeGroupNumber === undefined) {
        this.moduleContainerStatuses[moduleContainerId].activeGroupNumber = groupNumber;
      }
    }
  }

  private groupModulesByGroupNumber(modules: Module[]): ModulesByGroup {
    return modules
      .filter(m => this.isEmptyStateCandidate(m))
      .reduce((acc, module) => {
        const groupNumber = module.empty_state_data!.group_number;
        acc[groupNumber] ||= [];
        acc[groupNumber].push(module);
        return acc;
      }, {} as ModulesByGroup);
  }

  private analyzeGroup(groupModules: Module[]): GroupStatus {
    const hasModulesWithTitle = groupModules.some(m => !!m.empty_state_data?.show_title);
    const ajaxModules = groupModules.filter(m => m.ajax);
    const isPendingAjax = ajaxModules.length > 0 && ajaxModules.every(m => !m.empty_state_data?.ajax_response);

    if (isPendingAjax && this.noVisibleModules(groupModules)) {
      return { hasAjaxModules: true, status: EmptyStateStatus.PENDING_AJAX, hasModulesWithTitle };
    } else {
      return { hasAjaxModules: false, status: this.noVisibleModules(groupModules) ? EmptyStateStatus.SHOW : EmptyStateStatus.HIDE, hasModulesWithTitle };
    }
  }

  private isEmptyStateCandidate(module: Module): boolean {
    return !!(module.empty_state_data && module.empty_state_data.group_number !== undefined);
  }

  private noVisibleModules(modules: Module[]): boolean {
    return (modules || []).every(m => m.hidden || m.id === EmptyStateComponent.MODULE_ID);
  }

  private shouldShowInsideGroup(module: Module, groupStatus: GroupStatus): boolean {
    const hasTitleToShow = module.empty_state_data!.show_title && !!(module.title || module.subtitle || module.pretitle);
    return groupStatus?.status === EmptyStateStatus.SHOW && 
          (!groupStatus.hasModulesWithTitle || hasTitleToShow) &&
          (!groupStatus.showedModule || groupStatus.showedModule === module.unique_id);
  }
}